import { clientAuthApi } from './clientAuthApi';

type JsonBody = Record<string, unknown> | undefined;

/**
 * Dispatched when an API call receives a 401 and the token refresh fails.
 * The SessionExpiredModal listens for this event to show a user-friendly popup.
 */
export function emitSessionExpired() {
  window.dispatchEvent(new CustomEvent('session-expired'));
}

// HTTP client with authentication support
class HttpClient {
  private baseUrl: string;

  constructor() {
    // Use relative URLs by default so requests go to the same origin as the frontend.
    // This works on any device (mobile, desktop) via Vite proxy in dev, or a reverse
    // proxy / same-origin serving in production.
    // Set VITE_API_URL only when the API is served from a completely different domain.
    this.baseUrl = import.meta.env.VITE_API_URL || '';
  }

  /**
   * Get authentication headers
   */
  private getAuthHeaders(): HeadersInit {
    const token = localStorage.getItem('client_token');
    return token ? { 'Authorization': `Bearer ${token}` } : {};
  }

  /**
   * Check if token needs refresh and refresh if necessary
   */
  private async ensureValidToken(): Promise<string | null> {
    const token = localStorage.getItem('client_token');
    const refreshToken = localStorage.getItem('client_refresh_token');

    if (!token || !refreshToken) {
      return null;
    }

    // Check if token is expired
    if (clientAuthApi.isTokenExpired(token)) {
      try {
        const response = await clientAuthApi.refreshToken({ refreshToken });
        localStorage.setItem('client_token', response.token!);
        localStorage.setItem('client_refresh_token', response.refreshToken!);
        return response.token!;
      } catch (error) {
        // Refresh failed, clear tokens and notify UI
        localStorage.removeItem('client_token');
        localStorage.removeItem('client_refresh_token');
        localStorage.removeItem('client_user');
        emitSessionExpired();
        return null;
      }
    }

    return token;
  }

  /**
   * Make authenticated request
   */
  async request<T>(
    endpoint: string,
    init?: Omit<RequestInit, 'body'> & { body?: JsonBody; requireAuth?: boolean }
  ): Promise<T> {
    const url = `${this.baseUrl}${endpoint}`;
    const requireAuth = init?.requireAuth !== false; // Default to true

    let headers: HeadersInit = {
      'Content-Type': 'application/json',
      ...(init?.headers || {}),
    };

    // Add authentication if required
    if (requireAuth) {
      const validToken = await this.ensureValidToken();
      if (validToken) {
        headers = { ...headers, 'Authorization': `Bearer ${validToken}` };
      }
    }

    const response = await fetch(url, {
      ...init,
      headers,
      body: init?.body !== undefined ? JSON.stringify(init.body) : undefined,
    });

    const contentType = response.headers.get('content-type') || '';
    const isJson = contentType.includes('application/json');
    const data = isJson ? await response.json() : (undefined as unknown as T);

    if (!response.ok) {
      // Handle authentication errors
      if (response.status === 401 && requireAuth) {
        // Clear invalid tokens
        localStorage.removeItem('client_token');
        localStorage.removeItem('client_refresh_token');
        localStorage.removeItem('client_user');
        // Notify the UI so the session-expired modal can be shown
        emitSessionExpired();
      }
      throw Object.assign(new Error('HTTP error'), { status: response.status, data });
    }

    return data as T;
  }

  /**
   * GET request
   */
  async get<T>(endpoint: string, requireAuth: boolean = true): Promise<T> {
    return this.request<T>(endpoint, { method: 'GET', requireAuth });
  }

  /**
   * POST request
   */
  async post<T>(endpoint: string, body?: JsonBody, requireAuth: boolean = true): Promise<T> {
    return this.request<T>(endpoint, { method: 'POST', body, requireAuth });
  }

  /**
   * PUT request
   */
  async put<T>(endpoint: string, body?: JsonBody, requireAuth: boolean = true): Promise<T> {
    return this.request<T>(endpoint, { method: 'PUT', body, requireAuth });
  }

  /**
   * DELETE request
   */
  async delete<T>(endpoint: string, requireAuth: boolean = true): Promise<T> {
    return this.request<T>(endpoint, { method: 'DELETE', requireAuth });
  }
}

// Export singleton instance
export const httpClient = new HttpClient();

// Legacy function for backward compatibility
async function fetchJSON<T>(url: string, init?: Omit<RequestInit, 'body'> & { body?: JsonBody; requireAuth?: boolean }): Promise<T> {
  const requireAuth = init?.requireAuth !== false; // Default to true
  
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    ...(init?.headers || {}),
  };
  
  // Add authentication if required
  if (requireAuth) {
    const token = localStorage.getItem('client_token');
    if (token) {
      (headers as Record<string, string>)['Authorization'] = `Bearer ${token}`;
    }
  }
  
  const response = await fetch(url, {
    ...init,
    headers,
    body: init?.body !== undefined ? JSON.stringify(init.body) : undefined,
  });
  
  const contentType = response.headers.get('content-type') || '';
  const isJson = contentType.includes('application/json');
  const data = isJson ? await response.json() : (undefined as unknown as T);
  
  if (!response.ok) {
    // Handle authentication errors
    if (response.status === 401 && requireAuth) {
      // Clear invalid tokens
      localStorage.removeItem('client_token');
      localStorage.removeItem('client_refresh_token');
      localStorage.removeItem('client_user');
      // Notify the UI so the session-expired modal can be shown
      emitSessionExpired();
    }
    throw Object.assign(new Error('HTTP error'), { status: response.status, data });
  }
  
  return data as T;
}

export { fetchJSON };


